// form pelanggan.jsx - ENHANCED VERSION WITH DUPLICATE ADDRESS MODAL
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../pages/auth/AuthContext';

import Footer from '../components/footer/index';
import Progress from '../components/pengecekan_alamat/progressIndicator';
import Step1 from '../components/pengecekan_alamat/step1';
import Step2 from '../components/pengecekan_alamat/step2';
import { useDarkMode } from "../components/darkmode/DarkModeContext";

// API Configuration
const API_BASE_URL = (import.meta.env.VITE_API_URL || 'http://localhost:5000') + '/api';

// ✅ NEW: Duplicate Address Modal Component
const DuplicateAddressModal = ({ isOpen, onClose, onProceed, addressInfo, isDarkMode }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div 
        className={`max-w-md w-full rounded-2xl p-6 shadow-2xl transform transition-all duration-300 ${
          isDarkMode 
            ? 'bg-gray-800 border border-gray-600' 
            : 'bg-white border border-gray-200'
        }`}
      >
        {/* Header with Icon */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center text-2xl mx-auto mb-4">
            ⚠️
          </div>
          <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Alamat Sudah Terdaftar
          </h3>
        </div>

        {/* Content */}
        <div className="space-y-4 mb-6">
          <p className={`text-center leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Alamat untuk customer ini sudah terdaftar di area yang sama. Anda dapat:
          </p>
          
          <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
            <h4 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Detail Alamat Existing:
            </h4>
            <div className="space-y-1 text-sm">
              {addressInfo?.fullAddress && (
                <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                  <span className="font-medium">Alamat:</span> {addressInfo.fullAddress}
                </p>
              )}
              {addressInfo?.city && (
                <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                  <span className="font-medium">Kota:</span> {addressInfo.city}
                </p>
              )}
              {addressInfo?.createdAt && (
                <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                  <span className="font-medium">Terdaftar:</span> {new Date(addressInfo.createdAt).toLocaleDateString('id-ID')}
                </p>
              )}
            </div>
          </div>

          <div className={`p-4 rounded-lg border-l-4 border-blue-500 ${isDarkMode ? 'bg-blue-500/10' : 'bg-blue-50'}`}>
            <p className={`text-sm ${isDarkMode ? 'text-blue-200' : 'text-blue-700'}`}>
              💡 <strong>Saran:</strong> Anda dapat menggunakan alamat yang sudah ada atau mengubah detail alamat untuk membuat alamat baru.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={onClose}
            className={`flex-1 px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
              isDarkMode 
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Ubah Alamat
          </button>
          <button
            onClick={onProceed}
            className="flex-1 px-4 py-2 bg-gradient-to-r from-[#1E9B4C] to-green-600 text-white rounded-xl font-medium hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-lg"
          >
            Lanjutkan dengan Alamat Ini
          </button>
        </div>

        {/* Close button (X) */}
        <button
          onClick={onClose}
          className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
            isDarkMode 
              ? 'text-gray-400 hover:text-white hover:bg-gray-700' 
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
          }`}
        >
          ✕
        </button>
      </div>
    </div>
  );
};

// Main Coverage Check Page Component
const CoverageCheckPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { isDarkMode } = useDarkMode(); 
  
  // ✅ NEW: State for duplicate address modal
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateAddressInfo, setDuplicateAddressInfo] = useState(null);

  // Modifikasi: Ambil token jika ada, tapi tidak wajib
  const authToken = token || localStorage.getItem('token') || sessionStorage.getItem('token') || null;
  
  // Modifikasi: Ambil data user jika login, jika tidak biarkan null
  const currentUser = user?.user || user?.profile || user || null;
  const userId = currentUser?._id || currentUser?.id || currentUser?.userId || null;
  
  const { selectedPackage } = location.state || {};
  
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    province: '',
    city: '',
    district: '',
    village: '',
    postalCode: '',
    fullAddress: '',
    coordinates: null 
  });
  
  const [currentStep, setCurrentStep] = useState(1);
  const [isChecking, setIsChecking] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [coverageResult, setCoverageResult] = useState(null);
  const [availablePackages, setAvailablePackages] = useState([]);
  const [errors, setErrors] = useState({});
  const [savedAddressId, setSavedAddressId] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [createdOrderId, setCreatedOrderId] = useState(null);
  const [selectedPackageName, setSelectedPackageName] = useState(null);

  useEffect(() => {
    if (currentUser) {
      // console.log('Pre-populating form with user data:', currentUser);
      setFormData(prev => ({
        ...prev,
        fullName: currentUser.fullName || currentUser.name || prev.fullName,
        phone: currentUser.phone || prev.phone,
        email: currentUser.email || prev.email,
      }));
    }
  }, [currentUser]);

  useEffect(() => {
  // Menghapus console.log
}, [user, currentUser, userId, authToken]);

  const handleAddressSelect = (addressData) => {
    // console.log('Address selected from map:', addressData);
    
    setFormData(prev => ({
      ...prev,
      province: addressData.province,
      city: addressData.city,
      district: addressData.district,
      village: addressData.village,
      postalCode: addressData.postalCode,
      fullAddress: addressData.fullAddress 
        || addressData.formattedAddress 
        || addressData.address 
        || `${addressData.village || ''}, ${addressData.district || ''}, ${addressData.city || ''}`,
      coordinates: addressData.coordinates || null
    }));
    
    setSelectedLocation(addressData);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.fullName.trim()) newErrors.fullName = 'Nama lengkap wajib diisi';
      if (!formData.phone.trim()) newErrors.phone = 'Nomor telepon wajib diisi';
      if (!formData.email.trim()) newErrors.email = 'Email wajib diisi';
      else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email tidak valid';
    }
    
    if (step === 2) {
      if (!formData.fullAddress.trim()) newErrors.fullAddress = 'Silakan pilih lokasi pada peta';
      if (!formData.coordinates) newErrors.coordinates = 'Koordinat lokasi diperlukan';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < 3) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const apiService = {
    checkCoverage: async (city) => {
      await new Promise(resolve => setTimeout(resolve, 2000));
      return {
        success: true,
        data: {
          covered: Math.random() > 0.3,
          coverageStatus: 'available',
          message: `Area ${city} telah tercover layanan internet kami!`
        }
      };
    },
    
   getPackagesForCity: async (city) => {
  try {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    // Pertama, cek coverage kota
    const coverageCheck = await fetch(`${API_BASE_URL}/address/check-coverage`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ city })
    });

    const coverageResult = await coverageCheck.json();
    
    // Jika kota tidak tercover, jangan kembalikan paket apapun
    if (!coverageResult.success || !coverageResult.data.covered) {
      return { success: true, data: [] };
    }

    // Jika kota tercover, ambil paket yang tersedia
    const response = await fetch(`${API_BASE_URL}/packages/city/${encodeURIComponent(city)}`, {
      headers,
      credentials: 'include'
    });
    
    const result = await response.json();
    
    if (result.success) {
      let packages = result.data;
      
      packages = packages.map(pkg => ({
        ...pkg,
        _id: pkg._id || pkg.id || `pkg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        price: {
          ...pkg.price,
          displayPrice: pkg.price?.displayPrice || `Rp ${pkg.price?.amount?.toLocaleString('id-ID') || '0'}/bulan`
        }
      }));
      
      if (selectedPackage) {
        const matchingPackage = packages.find(pkg => pkg.name === selectedPackage.name);
        if (matchingPackage) {
          packages = [
            { ...matchingPackage, isPreSelected: true },
            ...packages.filter(pkg => pkg.name !== selectedPackage.name)
          ];
        } else {
          const selectedPkgWithId = {
            _id: selectedPackage._id || selectedPackage.id || `selected_${Date.now()}`,
            name: selectedPackage.name,
            speed: selectedPackage.speed,
            price: { displayPrice: selectedPackage.price },
            features: selectedPackage.features || [],
            isPreSelected: true
          };
          packages.unshift(selectedPkgWithId);
        }
      }
      
      return { success: true, data: packages };
    } else {
      throw new Error(result.message);
    }
  } catch (error) {
    console.error('Error fetching packages:', error);
    
    // Fallback ke data dummy hanya jika kota tercover
    // Anda bisa menambahkan logika untuk mengecek apakah kota termasuk COVERED_AREAS
    const coveredCities = ['Jakarta', 'Jakarta Timur', 'Jakarta Barat', 'Jakarta Selatan', 'Jakarta Utara', 'Jakarta Pusat', 'Banda Aceh', 'Aceh Barat', 'Lhokseumawe'];
    
    if (coveredCities.includes(city)) {
      let packages = [
        {
          _id: 'package_basic_001',
          name: 'Paket Home Basic',
          speed: '20 Mbps',
          price: { displayPrice: 'Rp 299.000/bulan' },
          features: ['Download 20 Mbps', 'Upload 10 Mbps', 'Unlimited Data', '24/7 Support']
        },
        {
          _id: 'package_premium_002',
          name: 'Paket Home Premium',
          speed: '50 Mbps',
          price: { displayPrice: 'Rp 499.000/bulan' },
          features: ['Download 50 Mbps', 'Upload 25 Mbps', 'Unlimited Data', '24/7 Support', 'Free Router']
        }
      ];

      if (selectedPackage) {
        const matchingPackage = packages.find(pkg => pkg.name === selectedPackage.name);
        if (matchingPackage) {
          packages = [
            { ...matchingPackage, isPreSelected: true },
            ...packages.filter(pkg => pkg.name !== selectedPackage.name)
          ];
        } else {
          const selectedPkgWithId = {
            _id: selectedPackage._id || selectedPackage.id || `selected_mock_${Date.now()}`,
            name: selectedPackage.name,
            speed: selectedPackage.speed,
            price: { displayPrice: selectedPackage.price },
            features: selectedPackage.features || [],
            isPreSelected: true
          };
          packages.unshift(selectedPkgWithId);
        }
      }

      return { success: true, data: packages };
    }
    
    return { success: true, data: [] };
  }
},
    
    // ✅ ENHANCED: saveAddress with improved duplicate handling
    saveAddress: async (addressData) => {
      try {
        // console.log('Saving address to database:', addressData);
        
        const addressPayload = {
          fullName: addressData.fullName || '',
          phone: addressData.phone || '',
          email: addressData.email || '',
          province: addressData.province || '',
          city: addressData.city || '',
          district: addressData.district || '',
          village: addressData.village || '',
          postalCode: addressData.postalCode || '',
          fullAddress: addressData.fullAddress || '',
          coordinates: addressData.coordinates || null,
          userId: userId || null
        };

        // console.log('Final address payload:', addressPayload);
        
        const headers = {
          'Content-Type': 'application/json',
        };
        
        if (authToken) {
          headers.Authorization = `Bearer ${authToken}`;
        }

        const response = await fetch(`${API_BASE_URL}/address`, {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify(addressPayload)
        });

        const result = await response.json();
        // console.log('Save address response:', result);

        // ✅ ENHANCED: Handle 409 Conflict with detailed error info
        if (response.status === 409) {
          const duplicateError = new Error(result.message || 'Alamat untuk customer ini sudah terdaftar di area yang sama');
          duplicateError.status = 409;
          duplicateError.duplicateData = {
            existingAddressId: result.data?.existingAddressId,
            fullAddress: result.data?.fullAddress || addressPayload.fullAddress,
            city: result.data?.city || addressPayload.city,
            createdAt: result.data?.createdAt,
            ...result.data
          };
          throw duplicateError;
        }

        if (result.success) {
          return result;
        } else {
          throw new Error(result.message || 'Failed to save address');
        }
      } catch (error) {
        console.error('Error saving address:', error);
        throw error;
      }
    }
  };

  // Fungsi untuk mengarahkan ke WhatsApp
  const redirectToWhatsApp = (packageName = null) => {
    const selectedPkg = packageName || selectedPackageName;
    const packageInfo = selectedPkg ? ` untuk paket ${selectedPkg}` : '';
    const message = `Halo, saya ${formData.fullName} tertarik dengan layanan internet${packageInfo} di area ${formData.city}. Alamat saya: ${formData.fullAddress}.`;
    const whatsappUrl = `https://wa.me/6287778882022?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleSelectPackage = (pkg) => {
    // console.log('Package selected:', pkg);
    setSelectedPackageName(pkg.name);
    
    // Update UI to show selected package
    setAvailablePackages(prev =>
      prev.map(p => ({
        ...p,
        isSelected: p._id === pkg._id
      }))
    );
    
    // Redirect to WhatsApp
    redirectToWhatsApp(pkg.name);
  };

  const checkCoverage = async () => {
  setIsChecking(true);
  setErrors({});
  
  try {
    // console.log(`Checking coverage for city: ${formData.city}`);
    
    // Gunakan API sebenarnya, bukan mock
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetch(`${API_BASE_URL}/address/check-coverage`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ city: formData.city })
    });

    const coverageResponse = await response.json();
    
    if (coverageResponse.success) {
      setCoverageResult(coverageResponse.data);
      // console.log('Coverage result:', coverageResponse.data);
      
      if (coverageResponse.data.covered) {
        // console.log('Area is covered, fetching available packages...');
        try {
          const packagesResponse = await apiService.getPackagesForCity(formData.city);
          if (packagesResponse.success) {
            // console.log('Available packages:', packagesResponse.data);
            setAvailablePackages(packagesResponse.data);
          } else {
            console.warn('Failed to fetch packages:', packagesResponse.message);
            setAvailablePackages([]);
          }
        } catch (packageError) {
          console.error('Error fetching packages:', packageError);
          setAvailablePackages([]);
        }
      } else {
        // console.log('Area is not covered, clearing packages');
        setAvailablePackages([]);
      }
    } else {
      throw new Error(coverageResponse.message || 'Coverage check failed');
    }
  } catch (error) {
    console.error('Coverage check failed:', error);
    setErrors({ coverage: 'Gagal mengecek coverage. Silakan coba lagi.' });
    setCoverageResult(null);
    setAvailablePackages([]);
  }
  
  setIsChecking(false);
  setCurrentStep(3);
};

  // ✅ NEW: Handle duplicate address modal actions
  const handleCloseDuplicateModal = () => {
    setShowDuplicateModal(false);
    setDuplicateAddressInfo(null);
    setIsSaving(false);
    // User can modify the address and try again
  };

  const handleProceedWithExistingAddress = async () => {
    try {
      // Use the existing address ID if available
      if (duplicateAddressInfo?.existingAddressId) {
        setSavedAddressId(duplicateAddressInfo.existingAddressId);
        // console.log(`Using existing address ID: ${duplicateAddressInfo.existingAddressId}`);
        
        setShowDuplicateModal(false);
        setDuplicateAddressInfo(null);
        setIsSaving(false);
        
        // Proceed to coverage check
        await checkCoverage();
      } else {
        throw new Error('Existing address ID not found');
      }
    } catch (error) {
      console.error('Error using existing address:', error);
      setErrors({ save: 'Gagal menggunakan alamat yang sudah ada. Silakan coba lagi.' });
      setShowDuplicateModal(false);
      setIsSaving(false);
    }
  };

  // ✅ ENHANCED: saveAddressAndProceed with duplicate handling
  const saveAddressAndProceed = async () => {
    setIsSaving(true);
    setErrors({}); // Clear previous errors
    
    try {
      if (!formData.fullAddress || !formData.fullAddress.trim()) {
        setErrors({ 
          save: 'Alamat lengkap wajib diisi. Silakan pilih lokasi pada peta terlebih dahulu.' 
        });
        setIsSaving(false);
        return;
      }

      const addressData = {
        fullName: formData.fullName || '',
        phone: formData.phone || '',
        email: formData.email || '',
        province: formData.province || '',
        city: formData.city || '',
        district: formData.district || '',
        village: formData.village || '',
        postalCode: formData.postalCode || '',
        fullAddress: formData.fullAddress || '',
        coordinates: formData.coordinates || null
      };

      // console.log('Saving address data:', addressData);

      // Modifikasi: Jika user tidak login, langsung lanjut ke pengecekan coverage
      if (!authToken) {
        // console.log('User not logged in, proceeding to coverage check without saving address');
        setIsSaving(false);
        await checkCoverage();
        return;
      }

      const response = await apiService.saveAddress(addressData);
      
      if (response.success) {
        setSavedAddressId(response.data._id);
        // console.log(`Alamat berhasil disimpan dengan ID: ${response.data._id}`);
        setIsSaving(false);
        await checkCoverage();
      }
    } catch (error) {
      console.error('Save address failed:', error);
      
      // ✅ NEW: Handle duplicate address error with modal
      if (error.status === 409) {
        // console.log('Duplicate address detected, showing modal');
        setDuplicateAddressInfo(error.duplicateData || {
          fullAddress: formData.fullAddress,
          city: formData.city
        });
        setShowDuplicateModal(true);
        // Don't set isSaving to false here, modal actions will handle it
      } else {
        // Handle other errors
        setErrors({ save: error.message || 'Gagal menyimpan alamat. Silakan coba lagi.' });
        setIsSaving(false);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      fullName: currentUser?.fullName || currentUser?.name || '',
      phone: currentUser?.phone || '',
      email: currentUser?.email || '',
      province: '',
      city: '',
      district: '',
      village: '',
      postalCode: '',
      fullAddress: '',
      coordinates: null
    });
    setCurrentStep(1);
    setCoverageResult(null);
    setAvailablePackages([]);
    setErrors({});
    setSavedAddressId(null);
    setSelectedLocation(null);
    setCreatedOrderId(null);
    setSelectedPackageName(null);
    // ✅ NEW: Reset modal states
    setShowDuplicateModal(false);
    setDuplicateAddressInfo(null);
  };

  // HAPUS: Blok kode yang memaksa login dihapus

  return (
    <div
      className={`min-h-screen bg-cover bg-center bg-no-repeat transition-all duration-500 ${
        isDarkMode
          ? "bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white"
          : "text-gray-900"
      }`}
      style={{
        backgroundImage: isDarkMode
          ? "none"
          : `url('https://telnet.net.id/assets/bg.png')`,
        backgroundColor: isDarkMode ? "#111827" : "#dfe4e0ff",
        backgroundBlendMode: isDarkMode ? "normal" : "overlay",
      }}
    >
      {/* ✅ NEW: Include the Duplicate Address Modal */}
      <DuplicateAddressModal 
        isOpen={showDuplicateModal}
        onClose={handleCloseDuplicateModal}
        onProceed={handleProceedWithExistingAddress}
        addressInfo={duplicateAddressInfo}
        isDarkMode={isDarkMode}
      />

      <style>
        {`
        .hero-gradient {
          background: linear-gradient(
            135deg, 
            #1a1a1a 0%, 
            #1E9B4C 50%,
            #1a1a1a 100%
          );
        }
        
        .card-gradient {
          background: linear-gradient(
            145deg, 
            #c3c3c3ff,       
            #c3c3c3ff
          );
          border: 1px solid rgba(139, 92, 246, 0.3);
        }
        
        .step-indicator {
          transition: all 0.3s ease;
        }
        
        .step-indicator.active {
          background: linear-gradient(
            135deg, 
            #10b981, 
            #1a1a1a
          );
          transform: scale(1.1);
          box-shadow: 0 0 20px rgba(139, 92, 246, 0.5);
        }
        
        .step-indicator.completed {
          background: linear-gradient(
            135deg, 
            #1E9B4C, 
            #1E9B4C
          );
        }
        
        .floating-animation {
          animation: float 6s ease-in-out infinite;
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        .search-glow:focus {
          box-shadow: 0 0 0 3px rgba(30, 155, 76, 0.3);
        }

        body {
          background-color: #dfe4e0ff;
        }

        .package-card {
          transition: all 0.3s ease;
          border: 2px solid transparent;
        }

        .package-card.selected {
          border-color: #1E9B4C;
          box-shadow: 0 0 15px rgba(30, 155, 76, 0.3);
          transform: translateY(-5px);
        }

        .package-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
        }
      `}
      </style>

      

      <section className="py-28">
        <div className="max-w-4xl mx-auto px-4">
          
          <Progress
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            isStepEnabled={(step) => {
              if (step === 2) return formData.fullName && formData.phone && formData.email;
              if (step === 3) return savedAddressId && formData.fullAddress && formData.coordinates;
              return true;
            }}
          />

          <div className="card-gradient rounded-3xl p-8">
            {currentStep === 1 && (
              <Step1
                formData={formData}
                errors={errors}
                handleInputChange={handleInputChange}
                handleNext={handleNext}
                userPreFilled={!!currentUser}
              />
            )}

            {currentStep === 2 && (
              <Step2
                formData={formData}
                errors={errors}
                selectedLocation={selectedLocation}
                handlePrevious={handlePrevious}
                saveAddressAndProceed={saveAddressAndProceed}
                isSaving={isSaving}
                checkCoverage={checkCoverage}
                isChecking={isChecking}
                onAddressSelect={handleAddressSelect}
              />
            )}
        
            {currentStep === 3 && (
              <div className="space-y-8">
                <div className="text-center mb-8">
                  <h3
                    className={`text-2xl font-bold mb-4 ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Hasil Coverage Check
                  </h3>
                  <p className={isDarkMode ? "text-gray-400" : "text-gray-700"}>
                    Berikut adalah hasil pengecekan coverage untuk lokasi Anda
                  </p>
                </div>

                {isChecking ? (
                  <div className="flex flex-col items-center justify-center py-16">
                    <div className="animate-spin w-12 h-12 border-4 border-[#1E9B4C] border-t-transparent rounded-full mb-6"></div>
                    <h4
                      className={`text-xl font-semibold mb-2 ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Sedang Mengecek Coverage...
                    </h4>
                    <p className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
                      Mohon tunggu sebentar, kami sedang menganalisa area Anda
                    </p>
                  </div>
                ) : coverageResult ? (
                  <div className="space-y-6">
                    {/* Coverage Status */}
                    <div
                      className={`rounded-2xl p-6 border-2 ${
                        coverageResult.covered
                          ? "bg-green-500/20 border-green-500/50"
                          : "bg-red-500/20 border-red-500/50"
                      }`}
                    >
                      <div className="flex items-center space-x-4">
                        <div
                          className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold text-white ${
                            coverageResult.covered ? "bg-green-500" : "bg-red-500"
                          }`}
                        >
                          {coverageResult.covered ? "✓" : "✗"}
                        </div>
                        <div className="flex-1">
                          <h4
                            className={`text-xl font-bold mb-2 ${
                              coverageResult.covered 
                                ? (isDarkMode ? "text-green-300" : "text-green-600")
                                : (isDarkMode ? "text-red-300" : "text-red-600")
                            }`}
                          >
                            {coverageResult.covered
                              ? "Area Tercover!"
                              : "Area Belum Tercover"}
                          </h4>
                          <p className={isDarkMode ? "text-gray-200" : "text-gray-800"}>
                            {coverageResult.message}
                          </p>
                          
                          {coverageResult.coverageDetails && (
                            <div className="mt-3 text-sm space-y-1">
                              {coverageResult.covered ? (
                                <>
                                  <p className={isDarkMode ? "text-gray-300" : "text-gray-600"}>
                                    Signal Strength: {coverageResult.coverageDetails.signalStrength}%
                                  </p>
                                  <p className={isDarkMode ? "text-gray-300" : "text-gray-600"}>
                                    Estimated Speed: {coverageResult.coverageDetails.estimatedSpeed}
                                  </p>
                                  <p className={isDarkMode ? "text-gray-300" : "text-gray-600"}>
                                    Installation Time: {coverageResult.coverageDetails.installationTime}
                                  </p>
                                </>
                              ) : (
                                <p className={isDarkMode ? "text-gray-300" : "text-gray-600"}>
                                  Kami akan segera melakukan ekspansi jaringan ke area ini.
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* User & Address Summary */}
                    <div
                      className={`rounded-2xl p-6 border ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    >
                      <h4 className="font-semibold mb-4">Detail Informasi</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-400">Nama:</span>
                          <span className="ml-2">{formData.fullName}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">Telepon:</span>
                          <span className="ml-2">{formData.phone}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">Email:</span>
                          <span className="ml-2">{formData.email}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">Kota:</span>
                          <span className="ml-2">{formData.city}</span>
                        </div>
                      </div>
                      <div className="mt-4">
                        <span className="text-gray-400">Alamat Lengkap:</span>
                        <p className="mt-1 text-sm leading-relaxed">
                          {formData.fullAddress}
                        </p>
                      </div>
                      {savedAddressId && (
                        <div className="mt-4 p-3 bg-green-500/20 rounded-lg border border-green-500/30">
                          <p className="text-green-300 text-sm">
                            ✓ Data telah disimpan dengan ID:{" "}
                            <span className="font-mono">{savedAddressId}</span>
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Conditional Package Display */}
                    {coverageResult.covered ? (
                      availablePackages.length > 0 ? (
                        <div className="space-y-4">
                          <h4
                            className={`text-xl font-bold ${
                              isDarkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            Pilih Paket Internet
                          </h4>
                          <p className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
                            Silakan pilih paket yang sesuai dengan kebutuhan Anda
                          </p>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {availablePackages.map((pkg) => (
                              <div
                                key={pkg._id}
                                className={`package-card rounded-2xl p-6 ${
                                  pkg.isSelected
                                    ? 'selected bg-gradient-to-br from-[#1E9B4C]/10 to-green-400/10'
                                    : `bg-gradient-to-br from-[#1E9B4C]/5 to-green-200/5 ${
                                        isDarkMode 
                                          ? 'bg-gray-800 border-gray-700' 
                                          : 'bg-white border-gray-200'
                                      }`
                                }`}
                              >
                                <h5
                                  className={`text-lg font-bold mb-2 ${
                                    isDarkMode ? "text-white" : "text-gray-900"
                                  }`}
                                >
                                  {pkg.name}
                                </h5>
                                <div className="text-sm font-semibold text-[#1E9B4C] mb-4">
                                  {pkg.speed}
                                </div>
                                <div className="space-y-2 mb-4">
                                  {pkg.features.map((feature, idx) => (
                                    <div
                                      key={idx}
                                      className="flex items-center space-x-2 text-sm"
                                    >
                                                                            <span className="w-2 h-2 bg-[#1E9B4C] rounded-full flex-shrink-0"></span>
                                      <span
                                        className={
                                          isDarkMode ? "text-gray-200" : "text-gray-800"
                                        }
                                      >
                                        {feature}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                                <button 
                                  onClick={() => handleSelectPackage(pkg)} 
                                  className="w-full mt-4 px-4 py-2 font-semibold rounded-xl transition-all bg-[#1E9B4C] text-white hover:bg-green-700 flex items-center justify-center"
                                >
                                  <span className="mr-2">Hubungi via WhatsApp</span>
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.864 3.488"/>
                                  </svg>
                                </button>
                              </div>
                            ))}
                          </div>

                          <div className="mt-6 p-4 bg-gradient-to-br from-blue-500/10 to-blue-200/10 rounded-2xl border border-blue-500/30">
                            <div className="text-center">
                              <h5 className="font-semibold mb-2">Butuh Bantuan Memilih Paket?</h5>
                              <p className="text-sm mb-4">
                                Tim kami siap membantu Anda memilih paket yang paling sesuai dengan kebutuhan Anda.
                              </p>
                              <button 
                                onClick={() => redirectToWhatsApp()}
                                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center mx-auto"
                              >
                                <span className="mr-2">Konsultasi Gratis</span>
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.864 3.488"/>
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div 
                          className={`rounded-2xl p-6 border ${
                            isDarkMode 
                              ? "bg-yellow-500/20 border-yellow-500/50" 
                              : "bg-yellow-100 border-yellow-300"
                          }`}
                        >
                          <div className="text-center">
                            <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center text-2xl font-bold text-white mx-auto mb-4">
                              !
                            </div>
                            <h4 
                              className={`text-lg font-bold mb-2 ${
                                isDarkMode ? "text-yellow-300" : "text-yellow-700"
                              }`}
                            >
                              Paket Belum Tersedia
                            </h4>
                            <p className={isDarkMode ? "text-gray-300" : "text-gray-600"}>
                              Area Anda sudah tercover, namun paket internet belum tersedia saat ini. 
                              Tim kami akan segera menambahkan paket untuk area ini.
                            </p>
                            <button 
                              onClick={() => redirectToWhatsApp()}
                              className="mt-4 px-6 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors flex items-center justify-center mx-auto"
                            >
                              <span className="mr-2">Hubungi Kami</span>
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.864 3.488"/>
                              </svg>
                            </button>
                          </div>
                        </div>
                      )
                    ) : (
                      <div 
                        className={`rounded-2xl p-6 border ${
                          isDarkMode 
                            ? "bg-blue-500/20 border-blue-500/50" 
                            : "bg-blue-100 border-blue-300"
                        }`}
                      >
                        <div className="text-center">
                          <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center text-2xl font-bold text-white mx-auto mb-4">
                            📡
                          </div>
                          <h4 
                            className={`text-lg font-bold mb-2 ${
                              isDarkMode ? "text-blue-300" : "text-blue-700"
                            }`}
                          >
                            Area Dalam Tahap Pengembangan
                          </h4>
                          <p className={isDarkMode ? "text-gray-300" : "text-gray-600"}>
                            Kami sedang merencanakan ekspansi jaringan ke area Anda. 
                            Silakan daftarkan minat Anda dan kami akan menginformasikan segera setelah layanan tersedia.
                          </p>
                          
                          <div className="mt-6 p-4 bg-white/10 rounded-lg">
                            <h5 className="font-semibold mb-2">Tertarik dengan layanan kami?</h5>
                            <p className="text-sm mb-4">
                              Tinggalkan informasi Anda dan kami akan menghubungi ketika layanan sudah tersedia di area ini.
                            </p>
                            <button 
                              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                              onClick={() => {
                                alert('Terima kasih! Data Anda telah kami catat. Tim kami akan menghubungi Anda ketika layanan sudah tersedia di area ini.');
                              }}
                            >
                              Daftarkan Minat Saya
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Error Display */}
                    {errors.order && (
                      <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
                        <p className="text-red-300">{errors.order}</p>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-4 pt-6">
                      <button
                        onClick={resetForm}
                        className="flex-1 px-6 py-3 bg-gray-500 text-white font-semibold rounded-xl hover:bg-gray-600 transition-all duration-300"
                      >
                        Cek Alamat Lain
                      </button>
                      {coverageResult.covered ? (
                        availablePackages.length > 0 ? (
                          <button 
                            onClick={() => redirectToWhatsApp()}
                            className="flex-1 px-6 py-3 bg-gradient-to-r from-[#1E9B4C] to-green-700 text-white font-semibold rounded-xl hover:from-green-700 hover:to-green-800 transition-all duration-300 flex items-center justify-center"
                          >
                            <span className="mr-2">Hubungi Kami</span>
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                                           <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.864 3.488"/>
                            </svg>
                          </button>
                        ) : (
                          <button 
                            onClick={() => redirectToWhatsApp()}
                            className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 flex items-center justify-center"
                          >
                            <span className="mr-2">Hubungi Tim Kami</span>
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.864 3.488"/>
                            </svg>
                          </button>
                        )
                      ) : (
                        <button 
                        className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white font-semibold rounded-xl hover:from-orange-700 hover:to-red-700 transition-all duration-300"
                        onClick={() => alert("Fitur ini masih dalam pengembangan. Kami akan segera menginformasikan ketika tersedia.")}
                      >
                        Request Coverage
                      </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
                      Belum ada hasil coverage. Silakan kembali ke langkah sebelumnya.
                    </p>
                    <button
                      onClick={() => setCurrentStep(2)}
                      className="mt-4 px-6 py-2 bg-[#1E9B4C] text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      Kembali ke Pemilihan Lokasi
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CoverageCheckPage;